// FIN-7296: a company admin transfers AI credits from their own wallet to an advisor of their structure.
// All backend figures are raw tokens (1 credit = 1.000 tokens, see TOKENS_PER_CREDIT); conversion is display-only.

/** The most recent transfer an advisor received (from `GET /ai/tokens/transfers/advisors/{id}/wallet`). */
export interface LastTokenTransferModel {
  readonly senderId: string;
  readonly tokens: number;
  readonly createdAt: string;
}

/** An advisor's wallet as seen by a company admin of their structure. */
export interface StructureAdvisorWalletModel {
  readonly advisorId: string;
  readonly grantedTokens: number;
  readonly consumedTokens: number;
  readonly remainingTokens: number;
  readonly lastTransfer: LastTokenTransferModel | null;
}

/** Body of `POST /ai/tokens/transfers`. */
export interface TokenTransferRequest {
  readonly recipientId: string;
  readonly credits: number;
}

/** Outcome of `POST /ai/tokens/transfers`: both resulting wallets. */
export interface TokenTransferResultModel {
  readonly senderId: string;
  readonly recipientId: string;
  readonly credits: number;
  readonly tokens: number;
  readonly senderGrantedTokens: number;
  readonly senderConsumedTokens: number;
  readonly senderRemainingTokens: number;
  readonly recipientGrantedTokens: number;
  readonly recipientConsumedTokens: number;
  readonly recipientRemainingTokens: number;
  readonly createdAt: string;
}

/** Tokens spent on one feature (and chat topic) — from `GET /ai/tokens/transfers/advisors/{id}/usage`. */
export interface FeatureUsageModel {
  readonly feature: string;
  readonly topic: string | null;
  readonly totalTokens: number;
  readonly requests: number;
}

/** Where an advisor's credits went over the last `months` calendar months (current month included). */
export interface AdvisorUsageBreakdownModel {
  readonly advisorId: string;
  readonly months: number;
  readonly fromMonth: string; // "2026-07"
  readonly toMonth: string; // "2026-09"
  readonly totalTokens: number;
  readonly features: readonly FeatureUsageModel[];
}

/** One row of the "Verbrauch nach Funktion" list: features folded into product buckets, in tokens. */
export interface UsageBucket {
  readonly label: string;
  readonly tokens: number;
  readonly share: number; // 0..100 of the total
}

const CHAT_TOPIC_LABELS: Record<string, string> = {
  CONTRACT_ANALYSIS: 'KI-Chat · Vertragscheck',
  CASHFLOW_ANALYSIS: 'KI-Chat · Cashflow',
  DOCUMENT_HELPER: 'KI-Chat · Dokumentenhilfe',
};

const FEATURE_LABELS: Record<string, string> = {
  CHAT: 'KI-Chat',
  CHAT_SUMMARY: 'KI-Chat',
  CONVERSATION_DIGEST: 'KI-Chat',
  DOCUMENT_EXTRACTION: 'Dokumente einlesen',
  CONTRACT_ANALYSIS: 'Vertragscheck',
  PORTFOLIO_ANALYSIS: 'Bestandsanalyse',
  CASHFLOW_ANALYSIS: 'Cashflow-Analyse',
  CLIENT_ANALYSIS: 'Kundenanalyse',
  CLIENT_DEEP_DIVE: 'Kundenanalyse',
  GENERIC: 'Sonstiges',
};

/** Product label for a ledger bucket; chat rows carry the topic, internal helpers fold into their product. */
export function usageLabel(feature: string, topic: string | null): string {
  const base: string = FEATURE_LABELS[feature] ?? 'Sonstiges';
  if (base === 'KI-Chat' && topic && CHAT_TOPIC_LABELS[topic]) {
    return CHAT_TOPIC_LABELS[topic];
  }
  return base;
}

/** Groups the raw feature/topic rows into labelled buckets, largest first. */
export function toUsageBuckets(breakdown: AdvisorUsageBreakdownModel): UsageBucket[] {
  const totals: Map<string, number> = new Map();
  for (const row of breakdown.features) {
    const label: string = usageLabel(row.feature, row.topic);
    totals.set(label, (totals.get(label) ?? 0) + row.totalTokens);
  }
  const total: number = breakdown.totalTokens || 1;
  return [...totals.entries()]
    .map(
      ([label, tokens]: [string, number]): UsageBucket => ({
        label,
        tokens,
        share: Math.round((tokens / total) * 100),
      }),
    )
    .sort((a: UsageBucket, b: UsageBucket): number => b.tokens - a.tokens);
}
